import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Shield, UserCog, Plus, Edit, Trash2, Users, Lock,
  ChevronDown, ChevronRight, Eye, Check, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RolesPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [builtinRoles, setBuiltinRoles] = useState({});
  const [customRoles, setCustomRoles] = useState([]);
  const [permCatalog, setPermCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editRole, setEditRole] = useState(null);

  const fr = language === 'fr';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [rolesRes, customRes, permRes] = await Promise.all([
        axios.get(`${API_URL}/api/roles/list`),
        axios.get(`${API_URL}/api/custom-roles`),
        axios.get(`${API_URL}/api/permissions/catalog`),
      ]);
      setBuiltinRoles(rolesRes.data);
      setCustomRoles(customRes.data);
      setPermCatalog(permRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await axios.delete(`${API_URL}/api/custom-roles/${roleId}`);
      toast.success(fr ? 'Role supprime' : 'Role deleted');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const levelColors = {
    platform: 'bg-red-100 text-red-700',
    university: 'bg-blue-100 text-blue-700',
    faculty: 'bg-purple-100 text-purple-700',
    department: 'bg-amber-100 text-amber-700',
    teaching: 'bg-green-100 text-green-700',
    admin: 'bg-slate-100 text-slate-700',
    student: 'bg-cyan-100 text-cyan-700',
    transport: 'bg-emerald-100 text-emerald-700',
    custom: 'bg-pink-100 text-pink-700',
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg" />)}</div>;

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="roles-title">
            {fr ? 'Gestion des Roles' : 'Role Management'}
          </h1>
          <p className="text-sm text-slate-500">
            {fr ? 'Roles integres et personnalises de votre universite' : 'Built-in and custom roles for your university'}
          </p>
        </div>
        {['super_admin', 'rector', 'sg_admin', 'it_admin'].includes(user?.role) && (
          <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="create-custom-role-btn">
            <Plus className="w-4 h-4" /> {fr ? 'Nouveau role' : 'New role'}
          </Button>
        )}
      </div>

      <Tabs defaultValue="builtin">
        <TabsList>
          <TabsTrigger value="builtin" className="gap-1.5" data-testid="tab-builtin">
            <Shield className="w-4 h-4" />{fr ? 'Roles integres' : 'Built-in roles'} ({Object.keys(builtinRoles).length})
          </TabsTrigger>
          <TabsTrigger value="custom" className="gap-1.5" data-testid="tab-custom">
            <UserCog className="w-4 h-4" />{fr ? 'Personnalises' : 'Custom'} ({customRoles.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="builtin" className="mt-4">
          <div className="grid gap-3">
            {Object.entries(builtinRoles).map(([key, role]) => (
              <Card key={key} data-testid={`role-${key}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900">{role.label_fr}</span>
                        <Badge className={`text-[10px] py-0 ${levelColors[role.level] || 'bg-slate-100'}`}>
                          {role.level}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{role.description_fr}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">
                    <Lock className="w-3 h-3 mr-1" />{role.permission_count} {fr ? 'perms' : 'perms'}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="custom" className="mt-4">
          {customRoles.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <UserCog className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">{fr ? 'Aucun role personnalise cree' : 'No custom roles created'}</p>
                <Button variant="outline" className="mt-4 gap-2" onClick={() => setShowCreate(true)}>
                  <Plus className="w-4 h-4" /> {fr ? 'Creer un role' : 'Create a role'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {customRoles.map(role => (
                <Card key={role.id} data-testid={`custom-role-${role.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                        <UserCog className="w-5 h-5 text-pink-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{role.name}</span>
                          <Badge variant="outline" className="text-[10px]">
                            <Users className="w-3 h-3 mr-1" />{role.user_count || 0}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500">{role.description || (fr ? 'Aucune description' : 'No description')}</p>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {(role.permissions || []).slice(0, 5).map(p => (
                            <Badge key={p} variant="secondary" className="text-[9px] py-0">{p}</Badge>
                          ))}
                          {(role.permissions || []).length > 5 && (
                            <Badge variant="secondary" className="text-[9px] py-0">+{role.permissions.length - 5}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditRole(role)} data-testid={`edit-role-${role.id}`}>
                        <Edit className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDeleteRole(role.id)} data-testid={`delete-role-${role.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {(showCreate || editRole) && (
        <CustomRoleDialog
          role={editRole}
          permCatalog={permCatalog}
          language={language}
          onClose={() => { setShowCreate(false); setEditRole(null); loadData(); }}
        />
      )}
    </div>
  );
};

const CustomRoleDialog = ({ role, permCatalog, language, onClose }) => {
  const fr = language === 'fr';
  const isEdit = !!role;
  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [selectedPerms, setSelectedPerms] = useState(role?.permissions || []);
  const [loading, setLoading] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  const toggleCat = (cat) => setExpandedCats(p => ({ ...p, [cat]: !p[cat] }));

  const togglePerm = (perm) => {
    setSelectedPerms(p => p.includes(perm) ? p.filter(x => x !== perm) : [...p, perm]);
  };

  const toggleAllInCat = (cat) => {
    const catPerms = permCatalog[cat].permissions.map(([k]) => k);
    const allSelected = catPerms.every(p => selectedPerms.includes(p));
    if (allSelected) {
      setSelectedPerms(prev => prev.filter(p => !catPerms.includes(p)));
    } else {
      setSelectedPerms(prev => [...new Set([...prev, ...catPerms])]);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error(fr ? 'Nom requis' : 'Name required'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/custom-roles/${role.id}`, { name, description, permissions: selectedPerms });
        toast.success(fr ? 'Role mis a jour' : 'Role updated');
      } else {
        await axios.post(`${API_URL}/api/custom-roles`, { name, description, permissions: selectedPerms });
        toast.success(fr ? 'Role cree' : 'Role created');
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? (fr ? 'Modifier le role' : 'Edit role') : (fr ? 'Nouveau role personnalise' : 'New custom role')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div>
            <Label>{fr ? 'Nom du role' : 'Role name'}</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder={fr ? 'Ex: Secretaire de jury' : 'E.g. Jury Secretary'} data-testid="role-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder={fr ? 'Description optionnelle...' : 'Optional description...'} data-testid="role-description-input" />
          </div>

          <div>
            <Label className="mb-2 block">{fr ? 'Permissions' : 'Permissions'} ({selectedPerms.length})</Label>
            <ScrollArea className="h-[300px] border rounded-lg p-2">
              {Object.entries(permCatalog).map(([catKey, cat]) => {
                const catPerms = cat.permissions.map(([k]) => k);
                const selectedCount = catPerms.filter(p => selectedPerms.includes(p)).length;
                const expanded = expandedCats[catKey];
                return (
                  <div key={catKey} className="mb-1">
                    <button
                      onClick={() => toggleCat(catKey)}
                      className="w-full flex items-center justify-between p-2 rounded-md hover:bg-slate-50 text-sm"
                      data-testid={`perm-cat-${catKey}`}
                    >
                      <div className="flex items-center gap-2">
                        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <span className="font-medium text-slate-700">{cat.label_fr}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{selectedCount}/{catPerms.length}</Badge>
                        <span
                          onClick={(e) => { e.stopPropagation(); toggleAllInCat(catKey); }}
                          className="text-[10px] text-blue-600 hover:underline cursor-pointer"
                        >
                          {selectedCount === catPerms.length ? (fr ? 'Tout retirer' : 'Remove all') : (fr ? 'Tout cocher' : 'Select all')}
                        </span>
                      </div>
                    </button>
                    {expanded && (
                      <div className="ml-6 space-y-1 pb-2">
                        {cat.permissions.map(([permKey, permLabel]) => (
                          <label key={permKey} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                            <Checkbox
                              checked={selectedPerms.includes(permKey)}
                              onCheckedChange={() => togglePerm(permKey)}
                              data-testid={`perm-${permKey}`}
                            />
                            <span className="text-slate-600">{permLabel}</span>
                            <span className="text-[10px] text-slate-400 ml-auto font-mono">{permKey}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </ScrollArea>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleSubmit} disabled={loading} data-testid="submit-role-btn">
              {loading ? '...' : isEdit ? (fr ? 'Sauvegarder' : 'Save') : (fr ? 'Creer' : 'Create')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RolesPage;
